import { BorrowerContractFunnelV2Dto, ComparateurPricingStatus } from "api/pricing"
import { MainBorrowerPricingState } from "../types"
import { MainBorrowerPricingSyncActions } from "./types"

/** Delay after a pricing request before pending cards are considered stuck. */
export const PRICING_REPLAY_DELAY_MS = 45_000
/** Delay after the replay before the cards still pending are marked as timed out. */
export const PRICING_GIVE_UP_DELAY_MS = 15_000
export const PRICING_UNAVAILABLE_MESSAGE = 'Tarification indisponible, relancez la recherche'

/** Replays the pricing request of the current wave and returns its contract funnels. */
export type PricingReplay = () => Promise<BorrowerContractFunnelV2Dto[] | null | undefined>

export interface PricingWatchdog {
  /** Starts watching the wave that was just requested (cancels the previous one). */
  arm: (replay: PricingReplay) => void
  /** Stops watching (new wave starting, unmount). */
  cancel: () => void
}

const hasPendingFunnels = (state: MainBorrowerPricingState) =>
  state.contractFunnels.some(funnel =>
    funnel.pricingStatus === ComparateurPricingStatus.Pending
    || funnel.pricingStatus === ComparateurPricingStatus.Loading)

/**
 * Safety net for pricing results pushed through SignalR: when a notification is lost the card
 * would stay pending forever. The request is replayed once (the back cache returns the results
 * computed meanwhile), then the cards still pending are marked as timed out.
 */
export const createPricingWatchdog = (
  actions: MainBorrowerPricingSyncActions,
  getState: () => MainBorrowerPricingState
): PricingWatchdog => {
  let timer: ReturnType<typeof setTimeout> | null = null
  let wave = 0

  const cancel = () => {
    wave += 1
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  const giveUp = (currentWave: number) => {
    timer = setTimeout(() => {
      timer = null
      if (currentWave !== wave || !hasPendingFunnels(getState())) return
      actions.markPendingFunnelsTimeout(PRICING_UNAVAILABLE_MESSAGE)
    }, PRICING_GIVE_UP_DELAY_MS)
  }

  const arm = (replay: PricingReplay) => {
    cancel()
    const currentWave = wave

    timer = setTimeout(async () => {
      timer = null
      if (currentWave !== wave || !hasPendingFunnels(getState())) return

      try {
        const contractFunnels = await replay()
        // A new wave started while the replay was in flight: its own watchdog takes over.
        if (currentWave !== wave) return
        if (contractFunnels) {
          actions.updateContractFunnels(contractFunnels)
        }
      } catch {
        if (currentWave !== wave) return
      }

      giveUp(currentWave)
    }, PRICING_REPLAY_DELAY_MS)
  }

  return { arm, cancel }
}
